import { Download, Printer } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { OfflineFallbackNotice } from "../components/common/OfflineFallbackNotice";
import { extractErrorMessage, fetchResourceList, fetchSettings } from "../lib/api";
import { loadOfflineSnapshot, saveOfflineSnapshot } from "../lib/offlineState";
import { printPdfUrl } from "../lib/pdfActions";

const INVOICE_BRAND = {
  logoSrc: "/invoice-logo.png",
  addressLines: ["4059 SANDWICH STREET", "WINDSOR, ON N9C4C3", "Canada"],
  phone: "(519) 737-7600",
  email: "billing@bullewadd.com",
  taxNumber: "851477638",
  terms: "Net 30 days",
  paymentMessage: "Payment should be made within 30 days.",
};

const formatCompanyPhone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : String(value || "").trim();
};

const buildCompanyBrand = (settings) => {
  const addressLines = String(settings?.company_address || "").split(/[\n,]+/).map((part) => part.trim()).filter(Boolean);
  return {
    ...INVOICE_BRAND,
    companyName: settings?.company_name || "Bullewadd Transport",
    addressLines: addressLines.length ? addressLines : INVOICE_BRAND.addressLines,
    phone: formatCompanyPhone(settings?.company_phone) || INVOICE_BRAND.phone,
    email: String(settings?.company_email || "").trim().toLowerCase() || INVOICE_BRAND.email,
  };
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");
const formatMoney = (value, currency) => new Intl.NumberFormat("en-CA", { style: "currency", currency: currency || "CAD", maximumFractionDigits: 2 }).format(value || 0);
const INVOICE_PRINT_CACHE_TTL_MS = 30 * 60 * 1000;
const buildInvoicePrintCacheKey = (loadIds) => `lumen-tms-invoice-print-v1:${loadIds.slice().sort().join(",")}`;

const customerLines = (load) => {
  const cityLine = [load?.customer_city, load?.customer_province_or_state].filter(Boolean).join(", ");
  const postalLine = [cityLine, load?.customer_postal_code].filter(Boolean).join(" ");
  return [load?.customer_name, load?.customer_billing_address, postalLine, load?.customer_country].filter(Boolean);
};

const chargeLines = (load) => [
  `Pickup: ${load?.pickup_company || "—"} — ${load?.pickup_city || "—"}`,
  `Pickup Date: ${formatDate(load?.planned_picked_at || load?.pickup_datetime)}`,
  `Delivery: ${load?.delivery_company || "—"} — ${load?.delivery_city || "—"}`,
  `Delivery Date: ${formatDate(load?.planned_delivered_at || load?.delivery_datetime)}`,
].filter(Boolean);

export default function InvoicePrintPage() {
  const [searchParams] = useSearchParams();
  const [invoiceData, setInvoiceData] = useState({ invoices: [] });
  const [loading, setLoading] = useState(true);
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false);
  const [cacheFallbackActive, setCacheFallbackActive] = useState(false);
  const loadIds = useMemo(() => (searchParams.get("load_ids") || "").split(",").filter(Boolean), [searchParams]);
  const shouldAutoPrint = searchParams.get("auto_print") === "1";
  const invoicePrintCacheKey = useMemo(() => buildInvoicePrintCacheKey(loadIds), [loadIds]);
  const [settings, setSettings] = useState(null);
  const brand = useMemo(() => buildCompanyBrand(settings), [settings]);

  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    setHasAutoPrinted(false);
  }, [loadIds, shouldAutoPrint]);

  useEffect(() => {
    const loadInvoices = async () => {
      setLoading(true);
      const cachedPayload = await loadOfflineSnapshot(invoicePrintCacheKey, INVOICE_PRINT_CACHE_TTL_MS);
      if (cachedPayload) {
        setInvoiceData(cachedPayload);
      }
      try {
        const payload = await fetchResourceList("/invoices/printable", { load_ids: loadIds.join(",") });
        setInvoiceData(payload);
        setCacheFallbackActive(false);
        await saveOfflineSnapshot(invoicePrintCacheKey, payload);
      } catch (error) {
        if (cachedPayload) {
          setCacheFallbackActive(true);
          toast.warning("Showing cached printable invoices while the connection recovers.");
        } else {
          toast.error(extractErrorMessage(error, "Unable to load printable invoices."));
        }
      } finally {
        setLoading(false);
      }
    };
    if (loadIds.length) {
      loadInvoices();
    } else {
      setLoading(false);
    }
  }, [invoicePrintCacheKey, loadIds]);

  const printableInvoices = invoiceData.invoices.filter((invoice) => loadIds.includes(invoice.load_id));
  const firstPrintableInvoice = printableInvoices[0] || null;

  useEffect(() => {
    if (!loading && shouldAutoPrint && firstPrintableInvoice?.file_url && !hasAutoPrinted) {
      setHasAutoPrinted(true);
      printPdfUrl(firstPrintableInvoice.file_url).catch((error) => {
        toast.error(extractErrorMessage(error, "Unable to open the invoice PDF for printing."));
      });
    }
  }, [firstPrintableInvoice, hasAutoPrinted, loading, shouldAutoPrint]);

  return (
    <div className="space-y-6 print:space-y-4" data-testid="invoice-print-page">
      <section className="flex flex-col gap-4 print:hidden lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Print view</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Printable invoices</h1>
          <p className="mt-2 text-sm text-zinc-500">This page now prints the generated invoice PDF instead of the HTML preview.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-70" data-testid="invoice-print-page-button" disabled={!firstPrintableInvoice?.file_url} onClick={() => printPdfUrl(firstPrintableInvoice.file_url).catch((error) => toast.error(extractErrorMessage(error, "Unable to open the invoice PDF for printing.")))} type="button"><Printer className="h-4 w-4" /> Print PDF</button>
          <Link className="rounded-2xl border border-zinc-200 px-5 py-3 text-sm font-semibold text-zinc-700" data-testid="invoice-print-page-back-link" to="/invoices">Back to invoices</Link>
        </div>
      </section>

      <OfflineFallbackNotice message={cacheFallbackActive ? "Showing cached printable invoices while the connection recovers." : ""} testId="invoice-print-offline-fallback-notice" />

      {loading ? <p className="text-sm text-zinc-500" data-testid="invoice-print-loading">Loading printable invoices...</p> : null}
      {!loading && !printableInvoices.length ? <p className="text-sm text-zinc-500" data-testid="invoice-print-empty">No printable invoices were found for this selection.</p> : null}

      <div className="space-y-6">
        {printableInvoices.map((invoice) => (
          <article className="rounded-3xl border border-zinc-200 bg-white p-8 shadow-sm print:break-after-page print:rounded-none print:border-0 print:p-0 print:shadow-none" data-testid={`invoice-print-card-${invoice.id}`} key={invoice.id}>
            <div className="grid gap-8 md:grid-cols-[1.45fr_0.95fr]">
              <div>
                <div className="h-44 w-44" data-testid={`invoice-print-logo-wrap-${invoice.id}`}>
                  <img alt={`${brand.companyName} logo`} className="h-full w-full object-contain object-left" data-testid={`invoice-print-logo-${invoice.id}`} src={brand.logoSrc} />
                </div>
                <div className="mt-2 space-y-1 text-sm text-zinc-600" data-testid={`invoice-print-company-block-${invoice.id}`}>
                  {brand.addressLines.map((line) => <p key={`${invoice.id}-${line}`}>{line}</p>)}
                  <p data-testid={`invoice-print-company-phone-${invoice.id}`}>Phone: {brand.phone}</p>
                  <p data-testid={`invoice-print-company-email-${invoice.id}`}>Email: {brand.email}</p>
                </div>
              </div>
              <div className="space-y-4 md:justify-self-end">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Invoice</p>
                  <h2 className="mt-2 text-3xl font-semibold text-zinc-900">{invoice.invoice_number}</h2>
                </div>
                <dl className="space-y-3 rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-5 text-sm" data-testid={`invoice-print-meta-${invoice.id}`}>
                  <div className="flex items-start justify-between gap-4"><dt className="font-semibold uppercase tracking-[0.14em] text-zinc-500">Date</dt><dd className="text-right font-semibold text-zinc-900">{invoice.invoice_date}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="font-semibold uppercase tracking-[0.14em] text-zinc-500">Cust Ref No.</dt><dd className="text-right font-semibold text-zinc-900">{invoice.load?.customer_reference_number || "—"}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="font-semibold uppercase tracking-[0.14em] text-zinc-500">Invoice No.</dt><dd className="text-right font-semibold text-zinc-900">{invoice.invoice_number}</dd></div>
                </dl>
              </div>
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <section className="rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid={`invoice-print-billto-${invoice.id}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Bill To</p>
                <div className="mt-3 space-y-1 text-sm text-zinc-700">
                  {customerLines(invoice.load).map((line) => <p key={`${invoice.id}-bill-${line}`}>{line}</p>)}
                </div>
              </section>
              <section className="rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid={`invoice-print-notes-${invoice.id}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Terms</p>
                <div className="mt-3 space-y-1 text-sm text-zinc-700">
                  <p>Net Terms: {brand.terms}</p>
                  <p>GST/HST #: {brand.taxNumber}</p>
                </div>
              </section>
            </div>

            <div className="mt-8 overflow-hidden rounded-3xl border border-zinc-200">
              <table className="min-w-full text-sm" data-testid={`invoice-print-table-${invoice.id}`}>
                <thead className="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                  <tr>
                    <th className="px-4 py-3">Pickup Date</th>
                    <th className="px-4 py-3">Shipment Details</th>
                    <th className="px-4 py-3 text-right">Unit</th>
                    <th className="px-4 py-3 text-right">Unit Rate</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="align-top text-zinc-700">
                    <td className="px-4 py-4">{formatDate(invoice.load?.planned_picked_at || invoice.load?.pickup_datetime)}</td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {chargeLines(invoice.load).map((line, index) => <p className={index === 0 ? "font-semibold text-zinc-900" : "text-zinc-600"} key={`${invoice.id}-charge-${line}`}>{line}</p>)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">1</td>
                    <td className="px-4 py-4 text-right">{formatMoney(invoice.load?.rate, invoice.load?.currency)}</td>
                    <td className="px-4 py-4 text-right font-semibold text-zinc-900">{formatMoney(invoice.load?.rate, invoice.load?.currency)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="space-y-2 text-sm text-zinc-600">
                <p>{brand.paymentMessage}</p>
              </div>
              <div className="min-w-[260px] rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-4" data-testid={`invoice-print-total-${invoice.id}`}>
                <div className="flex items-center justify-between gap-4 border-b border-zinc-200 pb-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{invoice.load?.currency || "CAD"} Total</p>
                  <p className="text-lg font-semibold text-zinc-900">{formatMoney(invoice.load?.rate, invoice.load?.currency)}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-4 border-t border-zinc-200 pt-4 md:flex-row md:items-center md:justify-between print:hidden">
              <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-700 print:hidden" data-testid={`invoice-print-download-${invoice.id}`} href={invoice.file_url} rel="noreferrer" target="_blank">
                <Download className="h-4 w-4" /> Download PDF
              </a>
              <p className="text-sm text-zinc-500">Load {invoice.load?.load_number} • {invoice.load?.pickup_company} to {invoice.load?.delivery_company}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}